// Shared menu model. The DB `pages` collection is the single source of truth for
// which pages exist; a persisted `menuTree` (in site settings) controls the
// ordering / parenting / visibility. Built-in groups plus admin custom groups
// provide the group containers. There is no longer a hardcoded "base page"
// registry — every page (built-in or custom) is a DB record and fully editable.

// Default top-level menu groups (containers only; pages nest inside by parent label).
export const DEFAULT_GROUPS = [
  { id: 'group:Members', label: 'Members', order: 1 },
  { id: 'group:Publications', label: 'Publications', order: 2 },
  { id: 'group:History', label: 'History', order: 5 },
];

// Build a registry of page/group nodes and a default tree order from the DB pages.
export function pagesRegistry({ pages, groups }) {
  const reg = {};
  (groups || []).forEach((g) => {
    reg[g.id] = { id: g.id, kind: 'group', label: g.label, custom: !!g.custom, order: g.order };
  });
  (pages || []).forEach((p) => {
    const pid = `page:${p.page_key}`;
    reg[pid] = {
      id: pid, kind: 'page',
      label: p.nav_label || p.title,
      path: p.path || `/p/${p.page_key}`,
      pageKey: p.page_key,
      custom: !p.builtin,
      draft: !!p.draft,
      publishAt: p.publish_at || null,
      parent: p.parent || 'top',
      order: typeof p.order === 'number' ? p.order : 999,
    };
  });

  // Default tree (used only when no menuTree is stored): interleave top-level
  // pages & groups by order, nesting pages whose parent matches a group label.
  const groupByLabel = {};
  Object.values(reg).forEach((n) => { if (n.kind === 'group') groupByLabel[n.label] = n; });

  const top = [];
  const topGroupById = {};
  // Custom pages always sort after built-ins in the default tree; a stable id
  // tie-breaker keeps equal-order records deterministic (fixes Reset ordering).
  const eff = (n) => (n.custom ? 100000 + (n.order || 0) : (n.order || 0));
  Object.values(reg).forEach((n) => {
    if (n.kind === 'group') {
      const t = { id: n.id, _order: n.order != null ? n.order : 999, children: [] };
      top.push(t);
      topGroupById[t.id] = t;
    }
  });
  Object.values(reg).forEach((n) => {
    if (n.kind !== 'page') return;
    const g = n.parent && n.parent !== 'top' && n.parent !== 'none' ? groupByLabel[n.parent] : null;
    if (g && topGroupById[g.id]) {
      topGroupById[g.id].children.push({ id: n.id, _order: eff(n) });
    } else if (n.parent === 'none') {
      // hidden from the menu (direct-link only) — omit from the default tree
    } else {
      top.push({ id: n.id, _order: eff(n) });
    }
  });
  const byOrder = (a, b) => a._order - b._order || String(a.id).localeCompare(String(b.id));
  top.sort(byOrder);
  top.forEach((t) => { if (t.children) t.children.sort(byOrder); });

  const strip = (t) => (t.children
    ? { id: t.id, children: t.children.map((c) => ({ id: c.id })) }
    : { id: t.id });
  const defaultTree = top.map(strip);
  return { reg, defaultTree };
}

// Resolve the stored compact menuTree into full nodes (labels/paths), appending
// any pages/groups not yet placed. Missing ids (e.g. deleted pages) are skipped.
export function resolveMenu({ pages, customPages, menuTree, customGroups }) {
  const allPages = pages || customPages || [];
  const groups = [
    ...DEFAULT_GROUPS,
    ...((customGroups || []).map((g) => ({ ...g, custom: true }))),
  ];
  const { reg, defaultTree } = pagesRegistry({ pages: allPages, groups });

  const tree = menuTree && menuTree.length ? menuTree : defaultTree;
  const seen = new Set();
  // Groups may nest other groups to any depth; a duplicate id is only kept once.
  const resolveNode = (n) => {
    const meta = reg[n.id];
    if (!meta || seen.has(n.id)) return null;
    seen.add(n.id);
    const node = { ...meta, hidden: !!n.hidden };
    if (meta.kind === 'group') {
      node.landing = n.landing || null;
      node.intro = n.intro || '';
      node.headerImage = n.headerImage || '';
      node.landingPath = groupLandingPath(node, reg);
      node.children = (n.children || []).map(resolveNode).filter(Boolean);
    }
    return node;
  };
  const resolved = tree.map(resolveNode).filter(Boolean);

  // Append pages / groups present in the registry but not yet in the stored tree.
  Object.values(reg).forEach((meta) => {
    if (seen.has(meta.id)) return;
    if (meta.kind === 'group') {
      resolved.push({ ...meta, hidden: false, landing: null, landingPath: null, children: [] });
      seen.add(meta.id);
      return;
    }
    if (meta.kind !== 'page') return;
    if (meta.parent && meta.parent !== 'top' && meta.parent !== 'none') {
      const g = findNode(resolved, (n) => n.kind === 'group' && n.label === meta.parent);
      if (g) { g.children = g.children || []; g.children.push({ ...meta, hidden: false }); seen.add(meta.id); return; }
    }
    if (meta.parent === 'none') { seen.add(meta.id); return; }
    resolved.push({ ...meta, hidden: false });
    seen.add(meta.id);
  });

  return resolved;
}

// Serialize resolved nodes back to the compact stored form (recursive).
export function serializeMenu(nodes) {
  return (nodes || []).map((n) =>
    n.kind === 'group'
      ? { id: n.id, hidden: !!n.hidden, landing: n.landing || null, intro: n.intro || '', headerImage: n.headerImage || '', children: serializeMenu(n.children) }
      : { id: n.id, hidden: !!n.hidden }
  );
}

// ---- Group landing pages ----
// A group may open a landing page when clicked: 'auto' (generated overview at
// /menu/<slug>) or 'page:<key>' (an existing page). null = dropdown only.
export function groupSlug(groupId) {
  return String(groupId).replace(/^group:/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function groupLandingPath(node, reg) {
  if (!node.landing) return null;
  if (node.landing === 'auto') return `/menu/${groupSlug(node.id)}`;
  const target = reg ? reg[node.landing] : null;
  return target && target.kind === 'page' ? target.path : null;
}

// Recursively drop hidden items and pages failing `showPage`; prune empty groups
// (a group with a landing page stays even when empty).
export function pruneMenu(nodes, showPage) {
  return (nodes || [])
    .filter((n) => !n.hidden)
    .map((n) => (n.kind === 'group' ? { ...n, children: pruneMenu(n.children, showPage) } : n))
    .filter((n) => (n.kind === 'group' ? n.children.length > 0 || !!n.landingPath : showPage(n)));
}

// Plain-text excerpt of a page for overview cards.
export function pageExcerpt(page, max = 150) {
  const raw = (page && (page.intro || page.body || page.html)) || '';
  const text = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max).replace(/\s+\S*$/, '')}…` : text;
}

// ---- Recursive tree helpers (shared by the admin editor, navbar & breadcrumbs) ----

// Depth-first search; returns the first node matching `pred` or null.
export function findNode(nodes, pred) {
  for (const n of nodes || []) {
    if (pred(n)) return n;
    if (n.children) { const hit = findNode(n.children, pred); if (hit) return hit; }
  }
  return null;
}

// Locate a node by id: { arr: containing array, index, node, parent (group node or null) }.
export function locateNode(nodes, id, parent = null) {
  for (let i = 0; i < (nodes || []).length; i++) {
    const n = nodes[i];
    if (n.id === id) return { arr: nodes, index: i, node: n, parent };
    if (n.children) { const hit = locateNode(n.children, id, n); if (hit) return hit; }
  }
  return null;
}

// Ancestor chain (top → nearest) of the node whose id matches, or null.
export function pathToNode(nodes, pred, trail = []) {
  for (const n of nodes || []) {
    if (pred(n)) return [...trail, n];
    if (n.children) { const hit = pathToNode(n.children, pred, [...trail, n]); if (hit) return hit; }
  }
  return null;
}

// Does `node` (a group) contain descendant `id`?
export function containsId(node, id) {
  return !!(node && node.children && locateNode(node.children, id));
}

// Flattened list of all group nodes with their depth (for "Move to…" pickers).
export function flattenGroups(nodes, depth = 0, out = []) {
  (nodes || []).forEach((n) => {
    if (n.kind === 'group') { out.push({ node: n, depth }); flattenGroups(n.children, depth + 1, out); }
  });
  return out;
}
